/**
 * Version Check Service
 *
 * This service periodically checks if the backend API version matches the frontend version.
 * When a version mismatch is detected, it triggers a page refresh to load the new version.
 *
 * Features:
 * - Periodic version checking via setInterval
 * - Automatic page refresh on version mismatch
 * - User notification before refresh
 * - Graceful error handling
 * - Configurable check interval
 */

import VERSION_CONFIG from '../config/version';
import { publicFetch } from '../utils/apiClient';

/**
 * Version check state
 */
let versionCheckInterval = null;
let isRefreshing = false;
let versionCheckPromise = null;
let pageShowHandler = null;
let visibilityChangeHandler = null;

/**
 * Callback function to notify about version mismatch
 * Will be set by the component that uses this service
 */
let onVersionMismatchCallback = null;

const getRefreshAttemptKey = (backendVersion, frontendVersion) =>
  `datacenterai:version-refresh:${frontendVersion}->${backendVersion}`;

const hasRefreshAlreadyBeenAttempted = (backendVersion, frontendVersion) => {
  try {
    return window.sessionStorage.getItem(getRefreshAttemptKey(backendVersion, frontendVersion)) === '1';
  } catch (error) {
    return false;
  }
};

const markRefreshAttempted = (backendVersion, frontendVersion) => {
  try {
    window.sessionStorage.setItem(getRefreshAttemptKey(backendVersion, frontendVersion), '1');
  } catch (error) {
    // If sessionStorage is unavailable, keep the existing one-shot in-memory guard.
  }
};

/**
 * Check if backend version matches frontend version
 * @returns {Promise<{match: boolean, backendVersion: string, frontendVersion: string}>}
 */
export const checkVersion = async () => {
  try {
    // Fetch backend version from /api/version endpoint
    const response = await publicFetch('/api/version', {
      // The version endpoint is specifically used to escape stale HTML/JS.
      // Never satisfy this request from the browser's HTTP cache.
      cache: 'no-store'
    });

    if (!response.ok) {
      return {
        match: true, // Assume match on error to avoid unnecessary refreshes
        backendVersion: 'unknown',
        frontendVersion: VERSION_CONFIG.FRONTEND_VERSION,
        error: `HTTP ${response.status}`
      };
    }

    const data = await response.json();
    const backendVersion = data.version;
    const frontendVersion = VERSION_CONFIG.FRONTEND_VERSION;

    const match = backendVersion === frontendVersion;


    return {
      match,
      backendVersion,
      frontendVersion
    };

  } catch (error) {
    return {
      match: true, // Assume match on error to avoid unnecessary refreshes
      backendVersion: 'unknown',
      frontendVersion: VERSION_CONFIG.FRONTEND_VERSION,
      error: error.message
    };
  }
};

const checkAndHandleVersion = async () => {
  // Initial load, interval, pageshow, and WeCom visibility events can overlap.
  // Coalesce them so one resume produces at most one network request.
  if (versionCheckPromise) {
    return versionCheckPromise;
  }

  versionCheckPromise = checkVersion()
    .then(result => {
      if (!result.match) {
        handleVersionMismatch(result.backendVersion, result.frontendVersion);
      }
      return result;
    })
    .finally(() => {
      versionCheckPromise = null;
    });

  return versionCheckPromise;
};

/**
 * Handle version mismatch - notify user and refresh page
 * @param {string} backendVersion - The backend version
 * @param {string} frontendVersion - The frontend version
 */
const handleVersionMismatch = (backendVersion, frontendVersion) => {
  // Prevent multiple simultaneous refreshes
  if (isRefreshing) {
    return;
  }

  isRefreshing = true;


  // Stop version checking to avoid multiple refreshes
  stopVersionCheck();

  const shouldAutoRefresh = !hasRefreshAlreadyBeenAttempted(backendVersion, frontendVersion);

  // Notify the UI component about version mismatch
  if (onVersionMismatchCallback) {
    onVersionMismatchCallback(backendVersion, frontendVersion, shouldAutoRefresh);
  }

  if (!shouldAutoRefresh) {
    return;
  }

  // Auto-refresh after delay
  markRefreshAttempted(backendVersion, frontendVersion);
  setTimeout(() => {
    window.location.reload();
  }, VERSION_CONFIG.AUTO_REFRESH_DELAY);
};

/**
 * Start periodic version checking
 * @param {function} onVersionMismatch - Callback function when version mismatch is detected
 */
export const startVersionCheck = (onVersionMismatch = null) => {
  // Don't start if version checking is disabled
  if (!VERSION_CONFIG.ENABLE_VERSION_CHECK) {
    return;
  }

  // Don't start if already running
  if (versionCheckInterval) {
    return;
  }

  // Set the callback
  onVersionMismatchCallback = onVersionMismatch;


  // Perform initial version check
  checkAndHandleVersion();

  // A normal browser can restore an SPA from its back-forward cache without
  // remounting React. Re-check only for that restored-page case.
  pageShowHandler = event => {
    if (event.persisted) {
      checkAndHandleVersion();
    }
  };
  window.addEventListener('pageshow', pageShowHandler);

  // WeCom commonly keeps its WebView alive and only toggles visibility when a
  // user reopens the workbench app. Restrict this extra check to WeCom so normal
  // browser tab switching does not create additional requests.
  if (/wxwork/i.test(window.navigator.userAgent)) {
    visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        checkAndHandleVersion();
      }
    };
    document.addEventListener('visibilitychange', visibilityChangeHandler);
  }

  // Set up periodic checking
  versionCheckInterval = setInterval(
    checkAndHandleVersion,
    VERSION_CONFIG.VERSION_CHECK_INTERVAL
  );
};

/**
 * Stop periodic version checking
 */
export const stopVersionCheck = () => {
  if (versionCheckInterval) {
    clearInterval(versionCheckInterval);
    versionCheckInterval = null;
  }
  if (pageShowHandler) {
    window.removeEventListener('pageshow', pageShowHandler);
    pageShowHandler = null;
  }
  if (visibilityChangeHandler) {
    document.removeEventListener('visibilitychange', visibilityChangeHandler);
    visibilityChangeHandler = null;
  }
};

/**
 * Manually trigger a version check (useful for testing)
 * @returns {Promise<void>}
 */
export const manualVersionCheck = async () => {
  return checkAndHandleVersion();
};

const versionCheckService = {
  checkVersion,
  startVersionCheck,
  stopVersionCheck,
  manualVersionCheck
};

export default versionCheckService;
